import { useEffect, useReducer, useRef, type ReactNode } from 'react'
import { Maintenance } from '../routes/errors/Maintenance'
import { emit } from '../platform/analytics/analytics'
import {
  getMaintenanceState,
  subscribeMaintenance,
  type MaintenanceState,
} from '../api/maintenanceStore'

/** The reducer only ever replaces the state wholesale — the store, not this
 *  component, decides what the next state is. `useReducer` over `useState` to
 *  match the pattern the rest of the app uses for state a hook outside React
 *  feeds in (`AuthProvider`'s `reduce` over `authMachine`'s events). */
function reduce(_current: MaintenanceState, next: MaintenanceState): MaintenanceState {
  return next
}

/**
 * The maintenance takeover — issue #274, decision 2.
 *
 * **A takeover, unlike `OfflineBanner`, and deliberately the opposite call.**
 * Offline leaves cached data readable because the tab still has everything it
 * loaded; planned maintenance means the BACKEND is down, so every request any
 * page makes fails identically and there is nothing left underneath worth
 * keeping on screen — a banner over a page that cannot load anything is not
 * more honest, it is a banner over a wall of broken panels.
 *
 * **Mounted inside `<BrowserRouter>`, wrapping `AuthProvider`, rather than
 * outside the router the way `AppTexture` and `OfflineBanner` are.**
 * `Maintenance.tsx` renders `<Link to="/">` for its brand mark, which throws
 * with no router above it — the same reason `AppCrashed` is built entirely on
 * `<a href>` instead. "Above the router" in the issue means above the ROUTING
 * TABLE and everything gated on auth, not above `<BrowserRouter>` itself: this
 * sits above `AuthProvider` and `AppRoutes`, so a maintenance window is shown
 * before any auth state or route is even evaluated, while staying inside the
 * one context `Maintenance`'s own markup needs.
 *
 * No context is exposed — nothing else in the app needs to read whether
 * maintenance is active, so this stays a gate rather than growing a second
 * `AuthContext`-shaped provider nobody consumes.
 */
export function MaintenanceGate({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reduce, undefined, getMaintenanceState)

  useEffect(() => subscribeMaintenance(() => dispatch(getMaintenanceState())), [])

  /**
   * `maintenance_shown` fires once per transition into an active notice, not
   * once per render — the same ref-guard shape `MfaChallenge.tsx` uses for
   * `mfa_challenge_shown`, because `Maintenance`'s own `useHitTarget` calls
   * re-render this tree on a breakpoint change without the notice itself
   * having changed.
   */
  const announced = useRef(false)
  useEffect(() => {
    if (state.active && !announced.current) {
      announced.current = true
      emit('maintenance_shown')
    } else if (!state.active) {
      announced.current = false
    }
  }, [state.active])

  if (state.active) return <Maintenance {...state.info} />
  return <>{children}</>
}
